import type { AttentionQueue, AttentionReason, AttentionRow } from '@/lib/api/attention';

/**
 * Presentation for each reason, and the order they appear in.
 *
 * Order is help-before-suspicion, matching the server: a stuck student is
 * losing time right now, while a student who pasted something is not going
 * anywhere and can be asked in an hour. Reversing it would turn a teaching tool
 * into an invigilation one.
 */
export const REASON_ORDER: AttentionReason[] = [
  'stuck',
  'matches',
  'arrived_whole',
  'behind',
  'inactive',
  'never_started',
];

/** Labels are the action, not a verdict about the student. */
export const REASON_LABEL: Record<AttentionReason, string> = {
  stuck: 'Stuck',
  matches: 'Matches classmates',
  arrived_whole: 'Arrived whole',
  behind: 'Falling behind',
  inactive: 'Inactive',
  never_started: 'Never started',
};

/**
 * A glyph as well as a colour, always.
 *
 * Roughly one man in twelve has a colour vision deficiency, and a placement
 * officer reading a printout has no colour at all.
 */
export const REASON_GLYPH: Record<AttentionReason, string> = {
  stuck: '◐',
  matches: '≈',
  arrived_whole: '⇱',
  behind: '↓',
  inactive: '○',
  never_started: '·',
};

/**
 * No red anywhere. Red means error, and being asked a question is not an error
 * — nor is being stuck, which is a normal part of learning to program.
 */
export const REASON_TONE: Record<AttentionReason, string> = {
  stuck: 'bg-amber-50 text-amber-700 border-amber-200',
  matches: 'bg-sky-50 text-sky-700 border-sky-200',
  arrived_whole: 'bg-sky-50 text-sky-700 border-sky-200',
  behind: 'bg-amber-50 text-amber-700 border-amber-200',
  inactive: 'bg-purple-50 text-purple-700 border-purple-200',
  never_started: 'bg-slate-100 text-slate-600 border-slate-200',
};

/** What the trainer is being asked to do — shown as the row's action hint. */
export const REASON_ACTION: Record<AttentionReason, string> = {
  stuck: 'Go and unblock them',
  matches: 'Ask them all together',
  arrived_whole: 'Ask them to talk it through',
  behind: 'Re-plan their week',
  inactive: 'Chase',
  never_started: 'Escalate',
};

/**
 * Rows matching a filter.
 *
 * Matches against EVERY reason, not just the headline. A student who is both
 * stuck and inactive must appear under either filter; matching only the
 * displayed reason would silently hide people, which is the one thing a triage
 * list must never do.
 */
export function filterRows(rows: AttentionRow[], reason: AttentionReason | 'all'): AttentionRow[] {
  if (reason === 'all') return rows;
  return rows.filter((r) => r.all_reasons.includes(reason));
}

export type QueueSort = 'urgency' | 'recent' | 'name';

/**
 * Order the queue.
 *
 * "Most urgent" is the server's order, which already ranks help above
 * suspicion; re-deriving it here would let the two drift. The other two exist
 * because a trainer arrives with a different question on different days — "who
 * did I not see this week" is a recency question, and "did I miss anyone" is an
 * alphabetical one.
 */
export function sortRows(rows: AttentionRow[], sort: QueueSort): AttentionRow[] {
  if (sort === 'urgency') return rows;
  const out = [...rows];
  if (sort === 'name') {
    out.sort((a, b) => a.name.localeCompare(b.name));
  } else {
    // Longest-idle first: the student nobody has seen is the one most easily
    // forgotten, which is precisely why they need surfacing.
    out.sort((a, b) => b.idle_days - a.idle_days || a.name.localeCompare(b.name));
  }
  return out;
}

/** Reasons with at least one student, in display order. */
export function activeReasons(queue: AttentionQueue): AttentionReason[] {
  return REASON_ORDER.filter((r) => (queue.counts[r] ?? 0) > 0);
}

/**
 * The line under the queue.
 *
 * It states what was NOT flagged on purpose. Silence has to read as "checked",
 * not "nobody looked" — otherwise a trainer quietly goes back to walking the
 * room and the queue is dead.
 */
export function summariseQueue(queue: AttentionQueue): string {
  if (queue.total === 0) return 'No students enrolled in this batch.';
  if (queue.flagged === 0) {
    return `Nothing outstanding — all ${queue.total} students are fine today.`;
  }
  const parts = [`${queue.clear} of ${queue.total} students have nothing outstanding`];
  if (queue.handled > 0) parts.push(`${queue.handled} marked handled`);
  return parts.join(' · ') + '.';
}
